import { Button, Container, Flex, Icon, IconButton, Text, useToast } from '@chakra-ui/react';
import { FaEye } from '@react-icons/all-files/fa/FaEye';
import { FaEyeSlash } from '@react-icons/all-files/fa/FaEyeSlash';
import { Form, Formik, FormikHelpers } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { decodeToken } from 'react-jwt';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import PasswordStrength from '../../components/PasswordStrength';
import SimpleFooter from '../../components/SimpleFooter';
import TextWithTooltip from '../../components/TextWithTooltip';
import Input from '../../components/formComponents/Input';
import { SUCCESS_TOASTS } from '../../constants/toasts';
import { ACCOUNT_LOGIN } from '../../constants/urls';
import {
  useCheckExistingAccountHasPasswordLazyQuery,
  useResetPasswordAndSendEmailMutation,
} from '../../gql/generated/graphql';
import { CookieKeys, useCookie } from '../../hooks/useCookie';
import { parseTokenFromHash, signupAndAuthorizeAsync } from '../../services/auth0';
import { logger } from '../../services/sentry';
import { EMAIL_KEY, JWTToken } from '../../utils/auth';
import { createSuccessToast } from '../../utils/toast';
import {
  passwordConfirmValidation,
  passwordSubmitErrorMessage,
  passwordValidation,
} from '../../utils/validation/auth';
import Title from './components/Title';
import { StrongPasswordTooltipExplanation } from './utils';

const validationSchema = Yup.object({
  password: passwordValidation,
  password_confirm: passwordConfirmValidation(),
});

interface Values {
  password: string;
  password_confirm: string;
}

const ResetPassword = () => {
  const history = useHistory();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [accessToken, setAccessToken] = useCookie<string>(CookieKeys.ACCESS_TOKEN);
  const [checkExistingAccountHasPassword] = useCheckExistingAccountHasPasswordLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [resetPasswordAndSendEmail] = useResetPasswordAndSendEmailMutation({
    context: {
      isErrorHandled: true,
    },
  });

  const alreadyRan = useRef(false);
  useEffect(() => {
    if (!window.location.hash || alreadyRan.current) {
      return;
    }

    alreadyRan.current = true;

    parseTokenFromHash({
      hash: window.location.hash,
      callback: (err, authResult) => {
        if (err || !authResult?.accessToken) {
          logger.error('ResetPassword.tsx', '', err, null);
          return;
        }

        setAccessToken(authResult.accessToken);
      },
    });
  }, [window.location.hash]);

  const handleSubmit = async (values: Values, formikHelpers: FormikHelpers<Values>) => {
    try {
      const { [EMAIL_KEY]: email } = decodeToken<JWTToken>(accessToken ?? '') ?? {};

      if (!email) {
        throw new Error('Something went wrong, please try again.');
      }

      const hasExistingPassword =
        (await checkExistingAccountHasPassword())?.data?.checkExistingAccountHasPassword ?? false;

      if (hasExistingPassword) {
        await resetPasswordAndSendEmail({
          variables: {
            password: values.password,
          },
        });
      } else {
        await signupAndAuthorizeAsync({
          email,
          password: values.password,
        });
      }

      toast(createSuccessToast(SUCCESS_TOASTS.passwordChanged));

      history.push(ACCOUNT_LOGIN);
    } catch (e) {
      formikHelpers.setErrors({ password: passwordSubmitErrorMessage(e as Error) });
    }
  };

  return (
    <Container pt={10}>
      <Title>Choose A New Password</Title>
      <Formik
        enableReinitialize
        initialValues={{
          password: '',
          password_confirm: '',
        }}
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={handleSubmit}
      >
        {({ values, isSubmitting }) => {
          return (
            <Form>
              <Flex
                maxW="400px"
                mx="auto"
                flexDirection="column"
                alignItems="center"
                bg="white"
                p={8}
                rounded="lg"
              >
                <Text color="leaseEndBlue" mb={1}>
                  Create a new password
                </Text>
                <TextWithTooltip label={StrongPasswordTooltipExplanation}>
                  Password requirements
                </TextWithTooltip>
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="New Password"
                  name="password"
                  placeholder="Password"
                  _container={{ my: 4 }}
                  icon={
                    <IconButton
                      variant="unstyled"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label="Hide/Show password"
                      icon={<Icon as={showPassword ? FaEye : FaEyeSlash} mt={1} />}
                    />
                  }
                />
                {values.password && (
                  <PasswordStrength h="auto" w="100%" password={values.password} />
                )}
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label="Confirm Password"
                  name="password_confirm"
                  placeholder="Confirm Password"
                  _container={{ mb: 10 }}
                  icon={
                    <IconButton
                      variant="unstyled"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      aria-label="Hide/Show password confirm"
                      icon={<Icon as={showConfirmPassword ? FaEye : FaEyeSlash} mt={1} />}
                    />
                  }
                />
                <Button w="100%" type="submit" isLoading={isSubmitting}>
                  SUBMIT
                </Button>
              </Flex>
              <SimpleFooter />
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default ResetPassword;
